/**
 * /market/:slug/combo/:comboSlug — Página de detalle de combo (PDP).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState, useMemo } from "react";
import { getRequest } from "@tanstack/react-start/server";
import {
  CheckCircle2, Package, XCircle,
} from "lucide-react";
import { addLineToStoredCart, type StoredCartAddon } from "@/lib/market-cart-storage";
import { showAddedToCart } from "@/lib/market-added-to-cart";
import {
  loadMarketCatalog,
  type MarketCatalogResult,
  type MarketProduct,
  type MarketVariant,
} from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { toast } from "sonner";
import { MarketPdpMediaGallery } from "@/components/market/MarketPdpMediaGallery";
import { MarketStoreChrome } from "@/components/market/MarketStoreChrome";
import { MarketBreadcrumb } from "@/components/market/MarketBreadcrumb";
import { MarketPdpSkeleton } from "@/components/market/MarketSkeletons";

// ─── Server fn ────────────────────────────────────────────────────────────────
// Cargamos el catálogo completo (que incluye combos en data.combos).
// El comboSlug se resuelve en el cliente desde data.combos.

const fetchComboCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

export const Route = createFileRoute("/market_/$slug/combo/$comboSlug")({
  pendingComponent: MarketPdpSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchComboCatalog({ data: params.slug }),
  component: ComboPdpPage,
  head: ({ params }) => ({
    meta: [{ title: `${params.comboSlug} — ${params.slug}` }],
    links: [
      { rel: "manifest", href: `/api/manifest/${params.slug}` },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
      },
    ],
  }),
});

// ─── Component ────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

function stockAvailable(product: MarketProduct, variantId?: string): boolean {
  if (product.variants && product.variants.length > 0 && variantId) {
    const v = product.variants.find((vv) => vv.id === variantId);
    if (!v) return false;
    if (!v.trackStock) return true;
    return (v.stockActual ?? 0) > 0;
  }
  if (!product.trackStock) return true;
  return (product.stockActual ?? 0) > 0;
}

function ComboPdpPage() {
  const { slug, comboSlug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  // counts: `${productId}:${variantId}` -> cantidad elegida de ese sabor/variante
  // (contador por sabor — un producto del combo con cantidad fija puede repartirse
  // entre varias variantes, ej. "12 empanadas" = 5 jamón y queso + 7 carne).
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [added, setAdded] = useState(false);

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">{result.error}</h1>
        <Link to="/market/$slug" params={{ slug }}
          className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          Volver a la tienda
        </Link>
      </div>
    );
  }

  const { tenant } = result.data;
  const combo = (result.data.combos ?? []).find((c) => c.slug === comboSlug);

  if (!combo) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Combo no encontrado</h1>
        <Link to="/market/$slug" params={{ slug }}
          className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          Volver a la tienda
        </Link>
      </div>
    );
  }

  const products = result.data.products.filter((p) => combo.items.some((ci) => ci.productId === p.id));

  const brandStyle = result.data.brand.surfaceStyle;

  // Imágenes: propias primero, luego de productos
  const comboImages: string[] = combo.images.filter(Boolean);
  const productImages: string[] = products.flatMap((p) => p.imagenUrl ? [p.imagenUrl] : []);
  const allImages = comboImages.length > 0 ? [...comboImages, ...productImages] : productImages;

  // Productos con ejes de variante
  const productsById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const countKey = (productId: string, variantId: string) => `${productId}:${variantId}`;

  const sumForProduct = (productId: string, variants: MarketVariant[]) =>
    variants.reduce((s, v) => s + (counts[countKey(productId, v.id)] ?? 0), 0);

  const incVariant = (productId: string, variant: MarketVariant, required: number) => {
    const productVariants = productsById.get(productId)?.variants ?? [];
    if (sumForProduct(productId, productVariants) >= required) return;
    const stockLimit = variant.trackStock ? (variant.stockActual ?? 0) : Infinity;
    const k = countKey(productId, variant.id);
    setCounts((c) => {
      const current = c[k] ?? 0;
      if (current >= stockLimit) return c;
      return { ...c, [k]: current + 1 };
    });
  };

  const decVariant = (productId: string, variant: MarketVariant) => {
    const k = countKey(productId, variant.id);
    setCounts((c) => {
      const current = c[k] ?? 0;
      if (current <= 0) return c;
      return { ...c, [k]: current - 1 };
    });
  };

  // Stock check: los productos sin variantes deben tener stock; los que tienen
  // variantes ya quedan acotados al stock de cada sabor vía incVariant().
  const allInStock = combo.items.every((ci) => {
    const p = productsById.get(ci.productId);
    if (!p) return false;
    const hasVariants = (p.variants?.length ?? 0) > 0 && (p.variantAxes?.length ?? 0) > 0;
    if (hasVariants) return true;
    return stockAvailable(p);
  });

  // Selección completa: para productos con variantes, la suma de sabores
  // elegidos debe alcanzar exactamente la cantidad fija del ítem del combo.
  const allSelectionsComplete = combo.items.every((ci) => {
    const p = productsById.get(ci.productId);
    if (!p) return false;
    const hasVariants = (p.variants?.length ?? 0) > 0 && (p.variantAxes?.length ?? 0) > 0;
    if (!hasVariants) return true;
    return sumForProduct(ci.productId, p.variants ?? []) === ci.quantity;
  });

  const canAdd = allInStock && allSelectionsComplete;

  const handleAdd = () => {
    if (!canAdd) {
      if (!allSelectionsComplete) toast.error("Completá la cantidad de sabores de cada producto");
      else toast.error("Algún producto del combo está sin stock");
      return;
    }

    // Build combo line as a virtual product
    const comboProduct: MarketProduct = {
      id: combo.id,
      name: combo.name,
      slug: combo.slug,
      categoriaId: null,
      categoriaName: "Combos",
      description: combo.description,
      imagenUrl: allImages[0] ?? null,
      tipoVenta: "unidad",
      precioUnitario: combo.price,
      precioPorKg: null,
      unidadMedida: "unidad",
      activo: combo.active,
      sortOrder: combo.sortOrder,
      tags: ["Combo"],
    };

    // Desglose del combo: mismo mecanismo de `addons`/`selections` ya validado
    // en checkout, comanda, recibo y detalle de pedido (ver #26). Para
    // productos con variantes se abre una línea por sabor elegido (#22).
    const addons: StoredCartAddon[] = [];
    for (const ci of combo.items) {
      const p = productsById.get(ci.productId);
      const hasVariants = !!p && (p.variants?.length ?? 0) > 0 && (p.variantAxes?.length ?? 0) > 0;
      if (hasVariants && p) {
        for (const v of p.variants ?? []) {
          const qty = counts[countKey(ci.productId, v.id)] ?? 0;
          if (qty <= 0) continue;
          addons.push({
            id: `${ci.id}:${v.id}`,
            addonProductId: ci.productId,
            name: p.name,
            qty,
            precioUnitario: 0,
            variantId: v.id,
            variantName: v.name,
          });
        }
      } else {
        addons.push({
          id: ci.id,
          addonProductId: ci.productId,
          name: p?.name ?? ci.productId,
          qty: ci.quantity,
          precioUnitario: 0,
        });
      }
    }

    addLineToStoredCart(slug, {
      product: comboProduct,
      qty: 1,
      addons,
      notes: addons.map((a) => `${a.qty}x ${a.name}${a.variantName ? ` (${a.variantName})` : ""}`).join(", "),
    });

    setAdded(true);
    showAddedToCart({
      title: combo.name,
      storeSlug: slug,
      imageUrl: combo.images?.[0] ?? null,
    });
  };

  return (
    <MarketStoreChrome
      slug={slug}
      data={result.data}
      activeHref={`/market/${slug}/combo/${comboSlug}`}
    >
      <div className="mx-auto max-w-5xl px-4 py-4 md:px-12 md:py-6">
        <MarketBreadcrumb
          items={[
            { label: tenant.name, href: `/market/${slug}` },
            { label: "Combos", href: `/market/${slug}` },
            { label: combo.name },
          ]}
          className="mb-6"
        />
        <div className="grid gap-6 md:grid-cols-2 md:gap-10">
          {/* Galería: flechas + zoom hover */}
          <MarketPdpMediaGallery
            images={allImages}
            alt={combo.name}
            badge={
              <span className="absolute left-3 top-3 z-10 rounded bg-[var(--market-accent)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--primary-foreground)]">
                Combo
              </span>
            }
          />

          {/* Info */}
          <div className="flex flex-col">
            <h1 className="market-font-display text-2xl font-extrabold leading-tight text-[var(--market-on-surface)] md:text-3xl">
              {combo.name}
            </h1>

            {/* Precio */}
            <div className="mt-3 flex items-baseline gap-3">
              <span className="market-font-display text-3xl font-bold text-[var(--market-accent)]">
                {fmt(combo.price)}
              </span>
              {combo.discountPct != null && combo.discountPct > 0 && (
                <span className="rounded bg-green-500/15 px-2 py-0.5 text-sm font-semibold text-green-600">
                  -{combo.discountPct}% OFF
                </span>
              )}
            </div>

            {/* Descripción */}
            {combo.description && (
              <p className="mt-4 text-sm leading-relaxed text-[var(--market-on-surface-variant)]">
                {combo.description}
              </p>
            )}

            {/* Disponibilidad general */}
            <div className="mt-4">
              {allInStock ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 px-3 py-1 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" /> Disponible
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 px-3 py-1 text-sm font-medium text-red-500">
                  <XCircle className="h-4 w-4" /> Sin stock (algún producto agotado)
                </span>
              )}
            </div>

            {/* Productos del combo */}
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-[var(--market-on-surface)] mb-3">
                Incluye:
              </h2>
              <div className="space-y-3">
                {combo.items.map((ci) => {
                  const p = productsById.get(ci.productId);
                  const axes = p?.variantAxes ?? [];
                  const variants = p?.variants ?? [];
                  const hasVariants = variants.length > 0 && axes.length > 0;
                  const itemSum = p ? sumForProduct(ci.productId, variants) : 0;
                  const itemInStock = p ? stockAvailable(p) : false;

                  return (
                    <div key={ci.id} className="rounded-xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-container)] p-3">
                      <div className="flex items-start gap-3">
                        {p?.imagenUrl ? (
                          <img src={p.imagenUrl} alt={p.name} className="h-12 w-12 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-[var(--market-surface-bright)] flex items-center justify-center shrink-0">
                            <Package className="h-5 w-5 text-[var(--market-on-surface-variant)]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-[var(--market-on-surface)] truncate">
                              {p?.name ?? ci.productId}
                            </p>
                            <span className="shrink-0 rounded-full bg-[var(--market-surface-variant)] px-2.5 py-0.5 text-xs font-bold text-[var(--market-on-surface)]">
                              ×{ci.quantity}
                            </span>
                          </div>
                          {hasVariants && p && (
                            <div className="mt-2 space-y-1.5">
                              <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-[var(--market-on-surface-variant)]">
                                  Elegí los sabores
                                </p>
                                <span
                                  className={`text-xs font-bold tabular-nums ${
                                    itemSum === ci.quantity ? "text-green-600" : "text-orange-500"
                                  }`}
                                >
                                  {itemSum}/{ci.quantity}
                                </span>
                              </div>
                              <div className="space-y-1.5">
                                {variants.map((v) => {
                                  const vCount = counts[countKey(p.id, v.id)] ?? 0;
                                  const stockLimit = v.trackStock ? (v.stockActual ?? 0) : Infinity;
                                  const outOfStock = stockLimit <= 0;
                                  return (
                                    <div
                                      key={v.id}
                                      className="flex items-center justify-between gap-2 rounded-lg border border-[var(--market-surface-variant)] px-2.5 py-1.5"
                                    >
                                      <span
                                        className={`text-xs font-medium ${
                                          outOfStock
                                            ? "text-[var(--market-on-surface-variant)] line-through"
                                            : "text-[var(--market-on-surface)]"
                                        }`}
                                      >
                                        {v.name}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          onClick={() => decVariant(p.id, v)}
                                          disabled={vCount <= 0}
                                          aria-label={`Quitar ${v.name}`}
                                          className="flex h-6 w-6 items-center justify-center rounded-full border border-[var(--market-surface-variant)] text-[var(--market-on-surface)] disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                          −
                                        </button>
                                        <span className="w-4 text-center text-xs font-bold tabular-nums">{vCount}</span>
                                        <button
                                          type="button"
                                          onClick={() => incVariant(p.id, v, ci.quantity)}
                                          disabled={outOfStock || itemSum >= ci.quantity}
                                          aria-label={`Agregar ${v.name}`}
                                          className="flex h-6 w-6 items-center justify-center rounded-full border border-[var(--market-accent)] text-[var(--market-accent)] disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {!itemInStock && !hasVariants && (
                            <p className="mt-1 text-xs text-red-500">Sin stock</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Botón agregar al carrito */}
            <div className="mt-8">
              <button
                type="button"
                disabled={!canAdd}
                onClick={handleAdd}
                className={`w-full rounded-2xl py-4 text-base font-bold transition-all ${
                  canAdd
                    ? "bg-[var(--market-accent)] text-[var(--primary-foreground)] hover:opacity-90 active:scale-[.98]"
                    : "bg-[var(--market-surface-variant)] text-[var(--market-on-surface-variant)] cursor-not-allowed opacity-60"
                }`}
              >
                {added ? "✓ Agregado al carrito" : `Agregar combo — ${fmt(combo.price)}`}
              </button>
              {!allSelectionsComplete && (
                <p className="mt-2 text-center text-xs text-orange-500">
                  Completá la cantidad de sabores de cada producto para continuar
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MarketStoreChrome>
  );
}
